'use client';

import { useRef } from 'react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@ps/ui/atoms/dropdown-menu';
import { Image as ImageIcon, Plus, ShoppingBag, Video } from 'lucide-react';

interface Props {
  onPickPhoto: (file: File) => void;
  onPickVideo: (file: File) => void;
  onShareOrder: () => void;
  disabled?: boolean;
}

/**
 * Composer `+` button — opens the "Share a Photo / Share a Video /
 * Share Order" menu (1:1 with PopshopRN's `ActionMenuButton`). Owns the
 * two hidden file inputs so the trigger and pickers live together; the
 * caller only sees the picked `File` / the order-picker request.
 */
export function ShareMenu({
  onPickPhoto,
  onPickVideo,
  onShareOrder,
  disabled,
}: Props) {
  const photoInputRef = useRef<HTMLInputElement | null>(null);
  const videoInputRef = useRef<HTMLInputElement | null>(null);

  const openPicker = (ref: React.RefObject<HTMLInputElement | null>) => {
    if (ref.current) {
      // Clear so re-picking the same file still fires `change`.
      ref.current.value = '';
      ref.current.click();
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Share"
            disabled={disabled}
            className="bg-surface-3 text-content-2 hover:text-content-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Plus className="h-5 w-5" aria-hidden />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          side="top"
          className="border-line text-content-1 bg-surface-1 min-w-[200px]"
        >
          <DropdownMenuItem
            onSelect={() => openPicker(photoInputRef)}
            className="focus:bg-surface-3 focus:text-content-1"
          >
            <ImageIcon className="mr-2 h-4 w-4" aria-hidden />
            Share a Photo
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => openPicker(videoInputRef)}
            className="focus:bg-surface-3 focus:text-content-1"
          >
            <Video className="mr-2 h-4 w-4" aria-hidden />
            Share a Video
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={onShareOrder}
            className="focus:bg-surface-3 focus:text-content-1"
          >
            <ShoppingBag className="mr-2 h-4 w-4" aria-hidden />
            Share Order
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <input
        ref={photoInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
        className="hidden"
        onChange={event => {
          const file = event.target.files?.[0];
          if (file) onPickPhoto(file);
        }}
      />
      <input
        ref={videoInputRef}
        type="file"
        accept="video/mp4,video/quicktime"
        className="hidden"
        onChange={event => {
          const file = event.target.files?.[0];
          if (file) onPickVideo(file);
        }}
      />
    </>
  );
}
